import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, SealCheck, House, Handshake, Clock,
  ShieldCheck, Calculator, Phone,
} from "@phosphor-icons/react";
import SiteNav from "@/components/SiteNav";
import { BRAND, LENDER } from "@/lib/constants";

function LenderPortrait({ className = "" }) {
  if (LENDER.photo) {
    return (
      <img
        src={LENDER.photo}
        alt={LENDER.name}
        className={`w-full h-full object-cover ${className}`}
        data-testid="lender-photo"
      />
    );
  }
  const initials = LENDER.name
    .split(" ")
    .filter(Boolean)
    .map((p) => p[0])
    .slice(0, 2)
    .join("");
  return (
    <div
      className={`flex h-full w-full items-center justify-center bg-gradient-to-br from-[#17181F] via-[#101116] to-brand/20 ${className}`}
      data-testid="lender-photo-placeholder"
      aria-label={`${LENDER.name} portrait coming soon`}
    >
      <span className="font-serif text-6xl md:text-7xl text-brand/80 tracking-wide">{initials}</span>
    </div>
  );
}

const STEPS = [
  {
    icon: Calculator,
    t: "Share your numbers",
    d: "Income, goals, and timeline — Jeff maps what you can comfortably afford.",
  },
  {
    icon: SealCheck,
    t: "Get pre-approved",
    d: "A real pre-approval letter that strengthens your offer when the right home appears.",
  },
  {
    icon: Handshake,
    t: "Close with Lamar",
    d: "Loan + real estate stay coordinated so nothing slips between contract and keys.",
  },
];

export default function Financing() {
  return (
    <div className="bg-background min-h-screen">
      <SiteNav />

      {/* Hero — brand + Jeff as co-leads of the page */}
      <header className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_0%,rgba(212,175,55,0.12),transparent_55%),radial-gradient(ellipse_at_90%_80%,rgba(255,255,255,0.04),transparent_45%)]" />
        <div className="relative max-w-7xl mx-auto px-6 pt-28 pb-16 md:pb-24">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="lg:col-span-7"
            >
              <p className="text-brand text-xs uppercase tracking-[0.3em] mb-4">Financing · Mortgage</p>
              <h1 className="text-4xl md:text-6xl text-white leading-[1.05] tracking-tight">
                Need a mortgage?<br />
                <span className="italic text-brand">Meet {LENDER.firstName}.</span>
              </h1>
              <p className="mt-6 text-lg text-white/60 font-light max-w-xl leading-relaxed">
                {LENDER.blurb}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href={LENDER.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="financing-apply-cta"
                  className="group inline-flex items-center justify-center gap-2 bg-brand text-black px-8 py-4 rounded-sm text-sm uppercase tracking-[0.1em] font-medium hover:bg-brand-hover transition-all"
                >
                  Start pre-approval <ArrowUpRight size={16} weight="bold" className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </a>
                <Link
                  to="/book"
                  data-testid="financing-book-cta"
                  className="inline-flex items-center justify-center gap-2 border border-white/20 text-white px-8 py-4 rounded-sm text-sm uppercase tracking-[0.1em] font-medium hover:border-brand hover:text-brand transition-all"
                >
                  Talk with {BRAND.agentFirst} first
                </Link>
              </div>
              <p className="mt-5 text-xs text-white/35">
                NMLS #{LENDER.nmls} · {LENDER.company} · Equal Housing Lender
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="lg:col-span-5"
            >
              <div className="relative aspect-[4/5] max-w-md mx-auto lg:ml-auto overflow-hidden rounded-sm border border-white/10 bg-[#101116]">
                <LenderPortrait />
                <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-black/85 via-black/50 to-transparent">
                  <p className="text-white text-2xl font-serif">{LENDER.name}</p>
                  <p className="text-white/60 text-sm mt-1">{LENDER.titleLong}</p>
                  <p className="text-brand text-xs uppercase tracking-[0.18em] mt-3">NMLS #{LENDER.nmls}</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </header>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <p className="text-brand text-xs uppercase tracking-[0.3em] mb-3">How it works</p>
        <h2 className="text-3xl md:text-4xl text-white mb-10 max-w-xl">
          Financing that moves at the speed of your offer.
        </h2>
        <div className="grid md:grid-cols-3 gap-8">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.t}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: i * 0.08 }}
              className="border-t border-white/10 pt-6"
            >
              <s.icon size={28} weight="duotone" className="text-brand mb-4" />
              <p className="text-xs text-white/30 uppercase tracking-[0.2em] mb-2">0{i + 1}</p>
              <h3 className="text-xl text-white mb-2">{s.t}</h3>
              <p className="text-sm text-white/50 font-light leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Specialties + trust */}
      <section className="border-y border-white/5 bg-[#101116]">
        <div className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <p className="text-brand text-xs uppercase tracking-[0.3em] mb-3">Programs</p>
            <h2 className="text-3xl text-white mb-6">Options that fit real NY buyers.</h2>
            <ul className="space-y-3">
              {LENDER.specialties.map((item) => (
                <li key={item} className="flex items-start gap-3 text-white/70">
                  <SealCheck size={20} weight="duotone" className="text-brand shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="space-y-6">
            <div className="flex gap-4">
              <ShieldCheck size={26} weight="duotone" className="text-brand shrink-0" />
              <div>
                <h3 className="text-white text-lg mb-1">Trusted handoff</h3>
                <p className="text-sm text-white/50 font-light leading-relaxed">
                  You stay with {BRAND.agentFirst} for the home search. Jeff handles the loan —
                  same team energy, clear ownership of each lane.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <Clock size={26} weight="duotone" className="text-brand shrink-0" />
              <div>
                <h3 className="text-white text-lg mb-1">Built for NYC timelines</h3>
                <p className="text-sm text-white/50 font-light leading-relaxed">
                  Competitive markets reward prepared buyers. Pre-approval first means you write
                  offers that sellers take seriously.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <House size={26} weight="duotone" className="text-brand shrink-0" />
              <div>
                <h3 className="text-white text-lg mb-1">Already shopping?</h3>
                <p className="text-sm text-white/50 font-light leading-relaxed">
                  Browse homes now, then start your pre-approval so you&apos;re ready when the right
                  listing hits.
                </p>
                <Link to="/listings" className="inline-flex items-center gap-2 text-brand text-sm mt-3 hover:gap-3 transition-all" data-testid="financing-browse-listings">
                  View listings <ArrowRight size={14} weight="bold" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="relative overflow-hidden rounded-sm border border-brand/25 bg-gradient-to-r from-brand/10 via-transparent to-transparent p-8 md:p-12">
          <p className="text-brand text-xs uppercase tracking-[0.3em] mb-3">Next step</p>
          <h2 className="text-3xl md:text-4xl text-white max-w-2xl leading-tight">
            Ready to see what you qualify for?
          </h2>
          <p className="mt-4 text-white/55 max-w-xl font-light">
            Start with {LENDER.firstName} on loanDepot — or book a call with {BRAND.agentFirst} if
            you want a game plan first.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href={LENDER.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="financing-apply-cta-bottom"
              className="inline-flex items-center justify-center gap-2 bg-brand text-black px-8 py-4 rounded-sm text-sm uppercase tracking-[0.1em] font-medium hover:bg-brand-hover transition-all"
            >
              Apply with {LENDER.firstName} <ArrowUpRight size={16} weight="bold" />
            </a>
            <a
              href={`tel:${BRAND.phoneDirect}`}
              data-testid="financing-call-lamar"
              className="inline-flex items-center justify-center gap-2 border border-white/20 text-white px-8 py-4 rounded-sm text-sm uppercase tracking-[0.1em] font-medium hover:border-brand hover:text-brand transition-all"
            >
              <Phone size={16} weight="bold" /> Call {BRAND.agentFirst}
            </a>
          </div>
          <p className="mt-8 text-[11px] text-white/30 max-w-2xl leading-relaxed">
            {LENDER.name} is a loan originator with {LENDER.company}, NMLS #{LENDER.nmls}.
            Mortgage products are offered by {LENDER.company}, not by {BRAND.broker} or {BRAND.agentName}.
            All loans subject to credit approval. Equal Housing Opportunity.
          </p>
        </div>
      </section>
    </div>
  );
}
